import React, { useState, ReactNode, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { OrderedMap } from 'immutable';
import classNames from 'classnames/bind';
import { IconChevronDownFill } from '@saucelabs/ds-icons';

import { ValueOf } from '../../../../globalTypes';
import { GlobalState } from '../../../../reducers';
import { trackEvent, MANUAL_EVENTS } from '../../../../tracking';
import { CONFIGURATION_TEST_TYPES } from '../../../../constants';
import { selectors as testConfigurationSelectors } from '../../../testConfiguration';
import FilterListCheckbox from './FilterListCheckbox';
import FilterListSwitch from './FilterListSwitch';
import Styles from './FilterColumn.styles';

const context = classNames.bind(Styles);

export type Filter = {
  active: boolean;
  disabled: boolean;
  greyedOut: boolean;
  label?: string;
};
// a filterCategory has more properties, just typing what we need here
export type FilterCategory = {
  items: OrderedMap<string, Filter>;
};
export type FilterColumnProps = FilterCategory & {
  filterCategory: string;
  headline: string;
  headlineIcon?: ReactNode;
  layout: 'checkbox' | 'switch';
  size?: 's' | 'm' | 'l';
  toggleItem: (key: string) => void;
};

const FilterColumn: React.FC<FilterColumnProps> = ({
  filterCategory,
  headline,
  headlineIcon = null,
  items,
  layout,
  size = 'm',
  toggleItem,
}) => {
  const [mobileHidden, setMobileHidden] = useState(true);
  const testType = useSelector<GlobalState, ValueOf<typeof CONFIGURATION_TEST_TYPES>>(
    testConfigurationSelectors.testTypeSelector,
  );

  const executeToggleItem = useCallback((item: string) => {
    const previousFilter = items.get(item);

    if (previousFilter) {
      trackEvent(MANUAL_EVENTS.FILTER_REAL_DEVICE, {
        filterCategory: headline.replace(':', ''),
        filterOn: !previousFilter.active,
        filterValue: item,
        testType,
      });
    }

    toggleItem(item);
  }, [items, toggleItem, headline, testType]);

  return (
    <div className={context('default', `size-${size}`)}>
      <div
        aria-expanded={!mobileHidden}
        className={`${Styles.headline} text-gray-600`}
        onClick={() => setMobileHidden(!mobileHidden)}
        role="button"
        tabIndex={-1}
      >
        {headlineIcon && (
          <div className={Styles['headline-icon']}>
            {headlineIcon}
          </div>
        )}

        {headline}
        <IconChevronDownFill className={context('caret', 'fill-current', 'text-gray-600', { active: !mobileHidden })} />
      </div>

      <div className={context('column', 'bg-white', { 'mobile-expanded': !mobileHidden })}>
        {layout === 'checkbox' && (
          <FilterListCheckbox
            filterCategory={filterCategory}
            filters={items}
            toggleItem={executeToggleItem}
          />
        )}

        {layout === 'switch' && (
          <FilterListSwitch
            filterCategory={filterCategory}
            filters={items}
            toggleItem={executeToggleItem}
          />
        )}
      </div>
    </div>
  );
};

export default FilterColumn;
